import React from 'react';
import ClientCard from './ClientCard';

const ClientList = ({ clients, onSelectClient, onNewMeeting, onUpdateClient, onDeleteClient, onAssignClient, canEdit = false, viewMode = 'grid', permissions, currentUser }) => {
  
  const renderListView = () => (
    <div className="overflow-hidden">
      {/* Header de la tabla con diseño premium */}
      <div className="bg-gradient-to-r from-[#0061A8] to-[#004d8a] px-6 py-4">
        <div className="hidden lg:grid lg:grid-cols-12 gap-4 text-sm font-semibold text-white">
          <div className="col-span-3">Cliente</div>
          <div className="col-span-2">Especialidad</div>
          <div className="col-span-1">Célula</div>
          <div className="col-span-2">Ejecutivo</div>
          <div className="col-span-1">Juntas</div>
          <div className="col-span-1">Conversión</div>
          <div className="col-span-2">Acciones</div>
        </div>
        <div className="lg:hidden text-white font-semibold">
          Lista de Clientes
        </div>
      </div>
      
      {/* Filas de datos con diseño responsivo */}
      <div className="divide-y divide-gray-100">
        {clients.map(client => {
          const totalContacts = client.meetings.reduce((total, m) => total + m.contactsGenerated, 0);
          const totalPatients = client.meetings.reduce((total, m) => total + m.patients, 0);
          const conversionRate = totalContacts > 0 ? ((totalPatients / totalContacts) * 100).toFixed(1) : '0.0';
          
          return (
            <div key={client.id} className="px-6 py-6 hover:bg-gradient-to-r hover:from-blue-50 hover:to-green-50 transition-all duration-300 group">
              {/* Vista Desktop */}
              <div className="hidden lg:grid lg:grid-cols-12 gap-4 items-center">
                {/* Cliente */}
                <div className="col-span-3">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-12 w-12">
                      <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#0061A8] to-[#004d8a] flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow">
                        <span className="text-lg font-bold text-white">
                          {client.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-semibold text-gray-900 group-hover:text-[#0061A8] transition-colors">{client.name}</div>
                      <div className="text-sm text-gray-500">{client.email}</div>
                    </div>
                  </div>
                </div>
                
                {/* Especialidad */}
                <div className="col-span-2">
                  <div className="text-sm font-medium text-gray-900">{client.specialty || 'No especificada'}</div>
                  <div className="text-sm text-gray-500">{client.country}</div>
                </div>
                
                {/* Célula */}
                <div className="col-span-1">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-[#A8D08D] to-[#8bc34a] text-white shadow-sm">
                    {Array.isArray(client.cells) ? client.cells.join(', ') : client.cell || 'N/A'}
                  </span>
                </div>
                
                {/* Ejecutivo */}
                <div className="col-span-2">
                  <div className="text-sm font-medium text-gray-900">
                    {Array.isArray(client.salesExecutives) 
                      ? client.salesExecutives.join(', ') 
                      : client.salesExecutive || 'No asignado'}
                  </div>
                </div>
                
                {/* Juntas */}
                <div className="col-span-1">
                  <div className="text-center">
                    <div className="text-lg font-bold text-[#0061A8]">{client.meetings.length}</div>
                    <div className="text-xs text-gray-500 font-medium">juntas</div>
                  </div>
                </div>
                
                {/* Conversión */}
                <div className="col-span-1">
                  <div className="text-center">
                    <div className={`text-lg font-bold ${
                      parseFloat(conversionRate) > 70 ? 'text-green-600' :
                      parseFloat(conversionRate) > 30 ? 'text-yellow-600' :
                      parseFloat(conversionRate) > 0 ? 'text-orange-600' : 'text-gray-400'
                    }`}>
                      {conversionRate}%
                    </div>
                    <div className="text-xs text-gray-500 font-medium">{totalPatients}/{totalContacts}</div>
                  </div>
                </div>
                
                {/* Acciones */}
                <div className="col-span-2">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => onSelectClient(client)}
                      className="group/btn flex items-center space-x-1 bg-gradient-to-r from-[#0061A8] to-[#004d8a] text-white px-3 py-2 rounded-lg text-xs font-semibold hover:from-[#004d8a] hover:to-[#0061A8] transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                    >
                      <svg className="w-4 h-4 group-hover/btn:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      <span>Ver</span>
                    </button>
                    {onNewMeeting && (
                      <button
                        onClick={() => onNewMeeting(client)}
                        className="group/btn flex items-center space-x-1 bg-gradient-to-r from-[#A8D08D] to-[#8bc34a] text-white px-3 py-2 rounded-lg text-xs font-semibold hover:from-[#8bc34a] hover:to-[#A8D08D] transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                      >
                        <svg className="w-4 h-4 group-hover/btn:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        <span>Junta</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Vista Mobile/Tablet */}
              <div className="lg:hidden space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-12 w-12">
                      <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#0061A8] to-[#004d8a] flex items-center justify-center shadow-lg">
                        <span className="text-lg font-bold text-white">
                          {client.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-lg font-semibold text-gray-900">{client.name}</div>
                      <div className="text-sm text-gray-500">{client.email}</div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => onSelectClient(client)}
                      className="bg-gradient-to-r from-[#0061A8] to-[#004d8a] text-white p-2 rounded-lg shadow-md"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                    {onNewMeeting && (
                      <button
                        onClick={() => onNewMeeting(client)}
                        className="bg-gradient-to-r from-[#A8D08D] to-[#8bc34a] text-white p-2 rounded-lg shadow-md"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <div className="text-xs text-gray-500 font-medium mb-1">Especialidad</div>
                    <div className="text-sm font-semibold text-gray-900">{client.specialty || 'N/A'}</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-xs text-gray-500 font-medium mb-1">Célula</div>
                    <div className="text-sm font-semibold text-gray-900">
                      {Array.isArray(client.cells) ? client.cells.join(', ') : client.cell || 'N/A'}
                    </div>
                  </div>
                  <div className="text-center p-3 bg-yellow-50 rounded-lg">
                    <div className="text-xs text-gray-500 font-medium mb-1">Juntas</div>
                    <div className="text-lg font-bold text-[#0061A8]">{client.meetings.length}</div>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <div className="text-xs text-gray-500 font-medium mb-1">Conversión</div>
                    <div className={`text-lg font-bold ${
                      parseFloat(conversionRate) > 70 ? 'text-green-600' :
                      parseFloat(conversionRate) > 30 ? 'text-yellow-600' :
                      parseFloat(conversionRate) > 0 ? 'text-orange-600' : 'text-gray-400'
                    }`}>
                      {conversionRate}%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderGridView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {clients.map(client => (
        <ClientCard
          key={client.id}
          client={client}
          onSelect={onSelectClient}
          onNewMeeting={onNewMeeting}
          onUpdateClient={onUpdateClient}
          onDeleteClient={onDeleteClient}
          canEdit={canEdit}
          currentUser={currentUser}
          permissions={permissions}
        />
      ))}
    </div>
  );

  return (
    <div className="space-y-4">
      {clients.length === 0 ? (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No hay clientes</h3>
          <p className="mt-1 text-sm text-gray-500">
            No se encontraron clientes que coincidan con los filtros aplicados.
          </p>
          <div className="mt-6">
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#0061A8] hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0061A8]"
              onClick={() => window.location.reload()}
            >
              <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Recargar página
            </button>
          </div>
        </div>
      ) : (
        <>
          {viewMode === 'list' ? renderListView() : renderGridView()}
        </>
      )}
    </div>
  );
};

export default ClientList;
